import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorCode } from '../models/error-code.enum';
import { GenericApiError } from '../models/generic-api-error';
import { MembershipReferral } from '../models/membership-referral';
import { UserDetails, UserDetailsAddress } from '../models/user-details';
import { UserEligibleResponseExternal } from '../models/user-eligible-response-external';
import { ValidationApiError } from '../models/validation-api-error';
import { LocalStorageKey } from './data-layer.service';

@Injectable({
  providedIn: 'root',
})
export class DataLayerUtilsService {
  /**
   * Turn any validation errors into an array of ValidationApiErrors for use in the rest of the front end
   * @param {HttpErrorResponse} errorResponse
   * @returns {GenericApiError}
   */
  public static extractValidationErrors(errorResponse: HttpErrorResponse): ValidationApiError[] {
    const error: any = errorResponse.error;

    if (!error) {
      return [];
    }

    if (typeof error === 'string') {
      return [
        {
          code: errorResponse.status.toString(),
          message: errorResponse.statusText || undefined,
          displayMessage: errorResponse.statusText || undefined,
          fieldName: '',
        },
      ];
    }

    if (error.non_field_errors) {
      return [
        {
          code: errorResponse.status.toString(),
          message: errorResponse.statusText || undefined,
          displayMessage: error.non_field_errors[0].message || undefined,
          fieldName: '',
        },
      ];
    }
    return this.collectValidationErrors(error, []);
  }

  /**
   * Recursively crawl error tree for any nested validation errors
   * @param error
   * @param {ValidationApiError[]} validationErrors
   * @returns {ValidationApiError[]}
   */
  private static collectValidationErrors(error: any, validationErrors: ValidationApiError[]): ValidationApiError[] {
    const apiErrorKeys = typeof error === 'object' && error !== null ? Object.keys(error) : [];

    apiErrorKeys
      .filter((key) => key !== 'non_field_errors')
      .forEach((key) => {
        if (Array.isArray(error[key])) {
          error[key].forEach((fieldError: any) => {
            validationErrors.push({
              code: fieldError.code || fieldError,
              message: fieldError.message || fieldError,
              displayMessage: fieldError.displayMessage || undefined,
              fieldName: key,
            });
          });
        } else {
          return DataLayerUtilsService.collectValidationErrors(error[key], validationErrors);
        }
      });
    return validationErrors;
  }

  /**
   * Extract the first error from the response and turn it into a GenericApiError object
   * @param error
   * @returns {GenericApiError}
   */
  public static extractGenericApiError(error: HttpErrorResponse): GenericApiError | undefined {
    let dataLayerError: GenericApiError = {
      code: ErrorCode.ApiError,
      message: 'generic api error',
      displayMessage: 'Something has gone wrong. Please try again',
    };

    // try and map any 400 errors to our own internal api data structure
    if ((error.error && error.status === 400) || error.status === 401) {
      const errorKeyName: string = Object.keys(error.error)[0];
      const isValidationError: boolean = errorKeyName !== 'non_field_errors';

      // validation errors are dealt with elsewhere
      if (isValidationError) {
        return undefined;
      }

      const apiError = error.error[errorKeyName][0];

      dataLayerError = {
        code: apiError.code,
        message: apiError.message,
        displayMessage: apiError.display_message,
      };
    }

    return dataLayerError;
  }

  public static extractClientData(response: UserEligibleResponseExternal): any {
    return {
      bank_identification_number: response.bank_identification_number,
      ica_code: response.ica_code,
      ica_business_name: response.ica_business_name,
      ica_legal_name: response.ica_legal_name,
      ica_region: response.ica_region,
      ica_state: response.ica_state,
      parent_ica_code: response.parent_ica_code,
      member_tier: response.member_tier,
      corporate_scheme_id: response.corporate_scheme_id,
      ica_country: response.ica_country,
      benefits: response.benefits,
      card_details: response.card_details,
    };
  }

  public static mapUserDetailsToHsbcJadeAndDBSRegisterRequest(
    userDetails: UserDetails,
    cachedData: any,
    reCaptchaToken: string
  ): any {
    const request: any = {
      ...cachedData,
      title: userDetails.title,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      email_address: userDetails.email,
      country_calling_code:
        userDetails.phoneNumber && userDetails.phoneNumber.countryCallingCode
          ? userDetails.phoneNumber.countryCallingCode.calling_code_url
          : undefined,
      phone_number: userDetails.phoneNumber ? userDetails.phoneNumber.number || undefined : undefined,
      address: {
        address_type: 'Main Home',
        house_number: userDetails.address.houseNumber || undefined,
        street_1: userDetails.address.street1 || undefined,
        street_2: userDetails.address.street2 || undefined,
        city: userDetails.address.city || undefined,
        state: userDetails.address.state || undefined,
        country: userDetails.address.country ? userDetails.address.country.url : undefined,
        postcode: userDetails.address.zipcode || undefined,
      },
      confirmed_terms: true,
      ...(userDetails.token && { token: userDetails.token }),
    };

    if (reCaptchaToken) {
      request['g-recaptcha-response'] = reCaptchaToken;
    }

    return request;
  }

  public static mapUserDetailsToVisaRegisterRequest(
    userDetails: UserDetails,
    cachedData: any,
    reCaptchaToken: string
  ): any {
    const request: any = {
      ...cachedData,
      title: userDetails.title,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      email_address: userDetails.email,
      address: this.getAddress(userDetails),
      birth_date: userDetails.birthDate ? userDetails.birthDate : undefined,
      country_calling_code:
        userDetails.phoneNumber && userDetails.phoneNumber.countryCallingCode
          ? userDetails.phoneNumber.countryCallingCode.calling_code_url
          : undefined,
      phone_number: userDetails.phoneNumber ? userDetails.phoneNumber.number || undefined : undefined,
      confirmed_terms:
        userDetails.optIns && userDetails.optIns['termsAndPrivacy']
          ? userDetails.optIns['termsAndPrivacy'].value
          : undefined,
    };

    if (reCaptchaToken) {
      request['g-recaptcha-response'] = reCaptchaToken;
    }

    return request;
  }

  public static getAddress(details: UserDetails): any {
    if (details?.address?.street1 && details?.address?.city && details?.address?.country) {
      return {
        address_type: 'Main Home',
        homeRoutes: details.address.houseNumber || undefined,
        street_1: details.address.street1 || undefined,
        city: details.address.city || undefined,
        state: details.address.state || undefined,
        country: details.address.country ? details.address.country.url : undefined,
        postcode: details.address.zipcode || undefined,
      };
    } else return undefined;
  }

  public static mapUserDetailsToMCRegisterRequest(
    userDetails: UserDetails,
    cachedData: any,
    reCaptchaToken?: string
  ): any {
    const request: any = {
      ...cachedData,
      title: userDetails.title,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      email_address: userDetails.email,
      country_calling_code:
        userDetails.phoneNumber && userDetails.phoneNumber.countryCallingCode
          ? userDetails.phoneNumber.countryCallingCode.calling_code_url
          : undefined,
      phone_number: userDetails.phoneNumber ? userDetails.phoneNumber.number || undefined : undefined,
      address: {
        address_type: 'Main Home',
        house_number: userDetails.address.houseNumber || undefined,
        street_1: userDetails.address.street1 || undefined,
        street_2: userDetails.address.street2 || undefined,
        city: userDetails.address.city || undefined,
        state: userDetails.address.state || undefined,
        country: userDetails.address.country ? userDetails.address.country.url : undefined,
        postcode: userDetails.address.zipcode || undefined,
      },
      confirmed_terms:
        userDetails.optIns && userDetails.optIns['termsAndPrivacy']
          ? userDetails.optIns['termsAndPrivacy'].value
          : undefined,
    };

    if (reCaptchaToken) {
      request['g-recaptcha-response'] = reCaptchaToken;
    }

    return request;
  }

  public static mapUserDataToVisaOAuthRegisterRequest(
    userDetails: UserDetails,
    cachedData: any,
    reCaptchaToken: string
  ): any {
    const request: any = {
      ...cachedData,
      state: userDetails.stateToken,
      card_number: userDetails.cardNumber,
      expiry_month: userDetails.expiryMonth,
      expiry_year: userDetails.expiryYear,
      security_code: userDetails.securityCode,
      title: userDetails.title,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      email_address: userDetails.email,
      country_calling_code:
        userDetails.phoneNumber && userDetails.phoneNumber.countryCallingCode
          ? userDetails.phoneNumber.countryCallingCode.calling_code_url
          : undefined,
      phone_number: userDetails.phoneNumber ? userDetails.phoneNumber.number || undefined : undefined,
      address: {
        address_type: 'Main Home',
        house_number: userDetails.address.houseNumber || undefined,
        street_1: userDetails.address.street1 || undefined,
        street_2: userDetails.address.street2 || undefined,
        city: userDetails.address.city || undefined,
        state: userDetails.address.state || undefined,
        country: userDetails.address.country ? userDetails.address.country.url : undefined,
        postcode: userDetails.address.zipcode || undefined,
      },
      confirmed_terms:
        userDetails.optIns && userDetails.optIns['termsAndPrivacy']
          ? userDetails.optIns['termsAndPrivacy'].value
          : undefined,
    };

    if (reCaptchaToken) {
      request['g-recaptcha-response'] = reCaptchaToken;
    }

    return request;
  }

  public static mapUserDetailsToRegisterRequest(userDetails: UserDetails, reCaptchaToken: string): any {
    const snakeCaseTransformer = (value: string) => {
      return value.replace(/[A-Z]/g, (x) => {
        return `_${x.toLowerCase()}`;
      });
    };

    const snake_case_opt_ins: any[] = [];

    if (userDetails.optIns) {
      Object.keys(userDetails.optIns).forEach((key: string) => {
        snake_case_opt_ins.push({
          field_name: snakeCaseTransformer(userDetails.optIns[key].fieldName),
          value: userDetails.optIns[key].value,
        });
      });
    }

    const user: any = {
      user_details: {
        title: userDetails.title,
        first_name: userDetails.firstName,
        last_name: userDetails.lastName,
        email: userDetails.email,
        phone_number:
          userDetails.phoneNumber && userDetails.phoneNumber.number
            ? {
                number: userDetails.phoneNumber.number,
                country_calling_code:
                  userDetails.phoneNumber && userDetails.phoneNumber.countryCallingCode
                    ? userDetails.phoneNumber.countryCallingCode.calling_code_url
                    : undefined,
              }
            : undefined,
      },
      opt_ins: snake_case_opt_ins,
    };

    const allFieldsEmpty = Object.keys(userDetails.address).every(
      (k: keyof UserDetailsAddress) => !userDetails.address[k]
    );

    if (userDetails.address && !allFieldsEmpty) {
      user.user_details.address = {
        address_type: 'Main Home',
        house_number: userDetails.address.houseNumber || undefined,
        street_1: userDetails.address.street1 || undefined,
        street_2: userDetails.address.street2 || undefined,
        city: userDetails.address.city || undefined,
        state: userDetails.address.state || undefined,
        country: userDetails.address.country ? userDetails.address.country.code : undefined,
        postcode: userDetails.address.zipcode || undefined,
      };
    }

    if (reCaptchaToken) {
      user['g-recaptcha-response'] = reCaptchaToken;
    }

    return user;
  }

  public static mapUserDetailsToGenericRegisterRequest(userDetails: UserDetails, reCaptchaToken: string): any {
    const eligibilityCheckToken = JSON.parse(localStorage.getItem(LocalStorageKey.Res));
    const user: any = {
      title: userDetails.title,
      first_name: userDetails.firstName,
      last_name: userDetails.lastName,
      email_address: userDetails.email,
      birth_date: userDetails.birthDate ? userDetails.birthDate : undefined,
      country_calling_code:
        userDetails.phoneNumber && userDetails.phoneNumber.countryCallingCode
          ? userDetails.phoneNumber.countryCallingCode.calling_code_url
          : undefined,
      confirmed_terms: true, // no tick box so it's an implicit confirm...
      token: eligibilityCheckToken ? eligibilityCheckToken.token : undefined,
    };

    if (userDetails.phoneNumber.number.length !== 0) {
      user.phone_number = userDetails.phoneNumber.number;
    }

    if (Object.keys(userDetails.address).some((key: keyof UserDetailsAddress) => !!userDetails.address[key])) {
      user.address = {
        address_type: 'Main Home',
        house_number: userDetails.address.houseNumber || undefined,
        street_1: userDetails.address.street1 || undefined,
        street_2: userDetails.address.street2 || undefined,
        city: userDetails.address.city || undefined,
        state: userDetails.address.state || undefined,
        country: userDetails.address.country ? userDetails.address.country.url : undefined,
        postcode: userDetails.address.zipcode || undefined,
      };
    }

    if (reCaptchaToken) {
      user['g-recaptcha-response'] = reCaptchaToken;
    }

    return user;
  }

  public static mapUserDetailsToCenturionRegisterRequest(userDetails: UserDetails, reCaptchaToken: string): any {
    let countryCallingCode = '';
    const { title, firstName, lastName, email, phoneNumber, yearOfBirth, swissBankAccount, registerInterest } =
      userDetails;

    if (phoneNumber.countryCallingCode && phoneNumber.countryCallingCode.calling_code) {
      countryCallingCode = phoneNumber.countryCallingCode.calling_code.toString();
    }

    const user: any = {
      title: title,
      first_name: firstName,
      last_name: lastName,
      email: email,
      phone_number: `${countryCallingCode}${phoneNumber.number}`,
      birth_year: yearOfBirth.toString(),
      is_swiss_bank_account: swissBankAccount,
      how_did_you_find_out: registerInterest,
    };

    if (reCaptchaToken) {
      user['g-recaptcha-response'] = reCaptchaToken;
    }

    return user;
  }

  public static mapUserDetailsToCenturionReferralRequest(
    membershipReferral: MembershipReferral,
    reCaptchaToken: string
  ): any {
    let countryCallingCode = '';

    if (
      membershipReferral.phoneNumber.countryCallingCode &&
      membershipReferral.phoneNumber.countryCallingCode.calling_code
    ) {
      countryCallingCode = membershipReferral.phoneNumber.countryCallingCode.calling_code.toString();
    }

    return {
      title: membershipReferral.title,
      first_name: membershipReferral.firstName,
      last_name: membershipReferral.lastName,
      email: membershipReferral.email,
      phone_number: `${countryCallingCode}${membershipReferral.phoneNumber.number}`,
      job_position: membershipReferral.jobPosition,
      entity_name: membershipReferral.entityName,
    };
  }
}
